import React, { useState, ComponentType, ReactNode } from "react"
import { NodeProps } from "reactflow"
import { Grid, Chip, Tooltip } from "@material-ui/core"
import { Text, themeColors } from "letalkui"

import AfterActionConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/AfterActionConditionSection"
import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import { Notification } from "@/components"

import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"
import useDidMount from "@/hooks/useDidMount"
import { ErrorType } from "@/hooks/useValidation"

import ManageContactInBoardSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/ManageContactsInBoardBlock/ManageContactInBoardSectionEditor"

import { BlockNodeData } from "@/protocols/chatBotFlow"
import { ManageContactInBoardActionToManageContactInBoardData } from "@/protocols/chatBot"

import BoardService, { BoardSummary } from "@/services/BoardService"
import ErrorHandlerService from "@/services/ErrorHandler"

import { getCurrentManageContactInBoardData, getCurrentManageContactInBoardOption } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"
import ColorUtil from "@/utils/ColorUtil"

const ManageContactsInBoardBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })

	const currentManageContactInBoardOption = getCurrentManageContactInBoardOption(chatBotFlowBlockDetails.content)

	const [boards, setBoards] = useState<Array<BoardSummary>>([])
	const [isLoading, setIsLoading] = useState<boolean>(true)

	const getInstanceBoards = async () => {
		setIsLoading(true)

		try {
			const instanceBoards = await BoardService.retrieveAllBoardsWithStagesNoCards()

			setBoards(instanceBoards)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			Notification.error({ message: "Não foi possível carregar os funis da instância." })
		}

		setIsLoading(false)
	}

	const getAddOrMoveContactToBoardLabel = () => {
		const currentManageContactInBoardData = getCurrentManageContactInBoardData(chatBotFlowBlockDetails.content) as ManageContactInBoardActionToManageContactInBoardData["add-or-move-contact-to-board-stage"]

		return (
			<Grid container alignContent="center" justifyContent="center" spacing={1}>
				<Grid item xs={12}>
					<Grid container justifyContent="flex-start">
						<Grid item xs>
							<Text.Body
								type="default"
								fontWeight="regular"
								color={themeColors.getTextColor()}
							>
								Funil:
							</Text.Body>
						</Grid>
						<Grid item xs={9}>
							{currentManageContactInBoardData?.board?.name ? (
								<Tooltip title={currentManageContactInBoardData.board.name}>
									<Chip
										label={(
											<Text.Body
												type="smaller1"
												fontWeight="bold"
												color={themeColors.colors.base.white}
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis"
												}}
											>
												{currentManageContactInBoardData.board.name}
											</Text.Body>
										)}
										size="small"
										style={{
											...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.bot.conditional[500]),
											borderRadius: "3px",
											maxWidth: "100%"
										}}
									/>
								</Tooltip>
							) : (
								<Chip
									label="Nenhum funil selecionado"
									size="small"
									style={{
										...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.neutral[50]),
										borderRadius: "3px",
										maxWidth: "100%"
									}}
								/>
							)}
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Grid container justifyContent="flex-start">
						<Grid item xs>
							<Text.Body
								type="default"
								fontWeight="regular"
								color={themeColors.getTextColor()}
							>
								Etapa:
							</Text.Body>
						</Grid>
						<Grid item xs={9}>
							{currentManageContactInBoardData?.stage?.name ? (
								<Tooltip title={currentManageContactInBoardData.stage.name}>
									<Chip
										label={(
											<Text.Body
												type="smaller1"
												fontWeight="bold"
												color={themeColors.colors.base.white}
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis"
												}}
											>
												{currentManageContactInBoardData.stage.name}
											</Text.Body>
										)}
										size="small"
										style={{
											...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.bot.conditional[500]),
											borderRadius: "3px",
											maxWidth: "100%"
										}}
									/>
								</Tooltip>
							) : (
								<Chip
									label="Nenhuma etapa selecionada"
									size="small"
									style={{
										...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.neutral[50]),
										borderRadius: "3px",
										maxWidth: "100%"
									}}
								/>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	const getRemoveContactFromBoardLabel = () => {
		const currentManageContactInBoardData = getCurrentManageContactInBoardData(chatBotFlowBlockDetails.content) as ManageContactInBoardActionToManageContactInBoardData["remove-contact-from-board"]

		return (
			<Grid container alignContent="center" justifyContent="center">
				{currentManageContactInBoardData?.board?.name ? (
					<Tooltip title={currentManageContactInBoardData.board.name}>
						<Chip
							label={(
								<Text.Body
									type="smaller1"
									fontWeight="bold"
									color={themeColors.colors.base.white}
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis"
									}}
								>
									{currentManageContactInBoardData.board.name}
								</Text.Body>
							)}
							size="small"
							style={{
								...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.bot.conditional[500]),
								borderRadius: "3px",
								maxWidth: "100%"
							}}
						/>
					</Tooltip>
				) : (
					<Chip
						label="Nenhum funil selecionado"
						size="small"
						style={{
							...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.neutral[50]),
							borderRadius: "3px",
							maxWidth: "100%"
						}}
					/>
				)}
			</Grid>
		)
	}

	const renderManageContactInBoardContent = () => {
		const manageContactInBoardActionToRenderBlockBodySectionFunction: Record<keyof ManageContactInBoardActionToManageContactInBoardData | "empty-manage-contact-in-board-action", () => ReactNode> = {
			"add-or-move-contact-to-board-stage": () => (
				<BlockBodySectionContainer
					text={getAddOrMoveContactToBoardLabel()}
					title="Adicionar contato ao"
					placeholder="Nenhuma ação criada"
					chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
					informationText="Obs: Se o contato já estiver nesta etapa, ele permanecerá nela. Caso esteja em outra etapa, será automaticamente movido para esta."
					errorType="missing_selected_stage_in_manager_contact_in_board"
				/>
			),
			"remove-contact-from-board": () => (
				<BlockBodySectionContainer
					text={getRemoveContactFromBoardLabel()}
					title="Remover contato do funil:"
					placeholder="Nenhuma ação criada"
					chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
					errorType="missing_selected_board_in_manager_contact_in_board"
				/>
			),
			"empty-manage-contact-in-board-action": () => (
				<BlockBodySectionContainer
					text={""}
					title="O que deseja fazer com o contato"
					placeholder="Nenhuma ação criada"
					chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
					errorType="missing_action_in_manager_contact_in_board"
				/>
			)
		}

		const currentManageContactInBoardAction = currentManageContactInBoardOption?.action

		let renderBlockBodySectionFunction: () => ReactNode

		if (!currentManageContactInBoardAction) {
			renderBlockBodySectionFunction = manageContactInBoardActionToRenderBlockBodySectionFunction["empty-manage-contact-in-board-action"]
		} else {
			renderBlockBodySectionFunction = manageContactInBoardActionToRenderBlockBodySectionFunction[currentManageContactInBoardAction]
		}

		if (renderBlockBodySectionFunction) {
			return renderBlockBodySectionFunction()
		}
	}

	useDidMount(() => {
		getInstanceBoards()
	})

	return (
		<BaseBlock node={node}>
			<BlockBodySection fullWidth>
				<ManageContactInBoardSectionEditor
					onSave={content => chatBotFlowBlockDetails.changeContent(content)}
					chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					boards={boards}
					isLoading={isLoading}
				>
					{renderManageContactInBoardContent()}
				</ManageContactInBoardSectionEditor>
			</BlockBodySection>

			<AfterActionConditionSection
				nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				text="Próximo passo"
			/>
		</BaseBlock>
	)
}

export default ManageContactsInBoardBlock
